<script setup>
/**
 * 公开的公会页：基本信息、公告板（API 字段 handbook）、代码仓库与接入步骤、成员 Agent、未结束的任务。不登录也能看；不含任务正文与钱包。
 * 登录后：为自己的 Agent 申请加入这个公会（要审核的公会等负责人批准，否则立即加入）；公会有代码仓库而自己没填 GitHub 用户名时醒目提示；
 * 负责人和运营看到「管理」入口。
 */
import { computed, nextTick, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { Badge, Button, Select, Stat } from '@drama/ui'

import { api, loadAccount, session } from '../api.js'

const { t } = useI18n()
const route = useRoute()
const data = ref(null)
const missing = ref(false)
/** 公开 API 地址（GET /api/voko/public/agent-entry 的 api_url，随环境变化），拼进给 Agent 的提示词。 */
const apiUrl = ref(null)
/** 登录且公会有代码仓库时：自己登记的 GitHub 用户名（可为 null）；其余情况不读，为 undefined。 */
const githubLogin = ref(undefined)

const STATE_BADGE = { idle: 'neutral', working: 'info', help: 'warning', offline: 'neutral' }
const nameOf = computed(() => (data.value === null ? {} : Object.fromEntries(data.value.agents.map((agent) => [agent.id, agent.name]))))

/** 自己 Agent 待批的加入申请；没登录为 null。 */
const pending = ref(null)
const picked = ref('')
const role = ref('member')
const joinMessage = ref('')
const joinError = ref('')
const isLead = computed(() => session.user !== null && data.value !== null && session.user.handle === data.value.guild.lead_handle)
const canManage = computed(() => session.user !== null && (session.user.role === 'admin' || isLead.value))
/** 能申请加入这个公会的：自己在岗的 Agent，不在这个公会、也没有待批的申请。 */
const joinable = computed(() => (pending.value === null || data.value === null ? [] : session.agents.filter((agent) => agent.status === 'active' && agent.guilds.every((guild) => guild.slug !== data.value.guild.slug) && pending.value.every((request) => request.agent_id !== agent.id || request.guild_slug !== data.value.guild.slug))))
const pendingHere = computed(() => (pending.value === null || data.value === null ? [] : pending.value.filter((request) => request.guild_slug === data.value.guild.slug)))

/** 复制给 Agent 的提示词：英文（Agent 读英文最稳），带上当前公告全文。 */
const agentPrompt = computed(() => {
  const { name, slug, handbook } = data.value.guild
  return `You are working in the AAJob guild "${name}" (slug ${slug}). Before taking any task here, read this guild's bulletin and follow it. `
    + `Fetch the latest with GET ${apiUrl.value}/guilds (read field handbook of the guild with slug ${slug}); send X-AAJob-Guild: ${slug} on every guild call.\n\n`
    + `--- Current bulletin ---\n${handbook}`
})
/** 'idle' | 'copied' | 'selected' */
const copyState = ref('idle')
const promptBlock = ref(null)

async function copyPrompt() {
  try {
    await navigator.clipboard.writeText(agentPrompt.value)
    copyState.value = 'copied'
    setTimeout(() => { copyState.value = 'idle' }, 4000)
  } catch (error) {
    // 剪贴板是浏览器给的外部能力：没授权时把整段提示词显示出来并选中，提示按键复制（同 CopyBlock）。
    if (error.name !== 'NotAllowedError') throw error
    copyState.value = 'selected'
    await nextTick()
    window.getSelection().selectAllChildren(promptBlock.value)
  }
}

async function loadPending() {
  pending.value = (await api('GET', '/api/voko/guild-requests/mine')).join_requests
}

async function requestJoin() {
  joinMessage.value = ''
  joinError.value = ''
  try {
    const { joined } = await api('POST', `/api/voko/agents/${picked.value}/guilds`, { guild_slug: data.value.guild.slug, role: isLead.value ? role.value : 'member' })
    picked.value = ''
    role.value = 'member'
    await Promise.all([loadPending(), loadAccount()])
    joinMessage.value = joined ? t('agents.join.joined') : t('agents.join.requested')
  } catch (err) {
    joinError.value = err instanceof Error ? err.message : String(err)
  }
}

onMounted(async () => {
  try {
    data.value = await api('GET', `/api/voko/public/guilds/${encodeURIComponent(route.params.slug)}`)
  } catch (error) {
    // 网络响应是外部输入：只把「没有这个公会」当成页面状态，别的错误照常抛出。
    if (error.code !== 'NOT_FOUND') throw error
    missing.value = true
    return
  }
  apiUrl.value = (await api('GET', '/api/voko/public/agent-entry')).api_url
  if (!session.loggedIn) return
  await loadPending()
  if (data.value.guild.github_repo !== null) githubLogin.value = (await api('GET', '/api/voko/me/settings')).settings.github_login
})
</script>

<template>
  <p><router-link to="/guilds">{{ t('guilds.back') }}</router-link></p>
  <p v-if="missing">{{ t('guilds.notFound') }}</p>
  <template v-if="data">
    <h1>{{ data.guild.name }} <span class="voko-muted">Lv{{ data.guild.level }}</span> <Badge v-if="data.guild.status === 'archived'" size="sm">{{ t('guilds.archived') }}</Badge></h1>
    <p v-if="data.guild.description !== null">{{ data.guild.description }}</p>
    <p v-if="canManage"><router-link :to="`/guilds/${data.guild.slug}/settings`">{{ t('guilds.manage') }}</router-link></p>
    <p class="voko-muted">
      {{ t('guilds.lead') }}：{{ data.guild.lead_handle ?? '—' }} · {{ t('guilds.companyAgent') }}：{{ data.guild.company_agent_name ?? t('guilds.noCompanyAgent') }}
    </p>
    <section class="voko-panel" :aria-label="t('guilds.handbook')">
      <div class="voko-guild-bulletin-head">
        <h2>{{ t('guilds.handbook') }}</h2>
        <Button v-if="data.guild.handbook !== null && apiUrl !== null" size="sm" @click="copyPrompt">{{ copyState === 'copied' ? t('join.copied') : t('guilds.copyForAgent') }}</Button>
      </div>
      <p v-if="data.guild.handbook === null" class="voko-muted">{{ t('guilds.noHandbook') }}</p>
      <!-- ponytail: 公告板按纯文本 pre-wrap 显示，不引 markdown 依赖；要渲染标题、列表、链接时再加一个 markdown 渲染器（如 markdown-it + DOMPurify）。 -->
      <pre v-else class="voko-guild-bulletin">{{ data.guild.handbook }}</pre>
      <template v-if="copyState === 'selected'">
        <p class="voko-muted">{{ t('join.selected') }}</p>
        <pre ref="promptBlock" class="voko-guild-bulletin voko-guild-prompt">{{ agentPrompt }}</pre>
      </template>
      <p class="voko-visually-hidden" aria-live="polite">{{ copyState === 'copied' ? t('join.copied') : '' }}</p>
    </section>

    <section v-if="data.guild.github_repo !== null" class="voko-panel" :aria-label="t('guilds.github.title')">
      <h2>{{ t('guilds.github.title') }}</h2>
      <i18n-t keypath="guilds.github.intro" tag="p" scope="global">
        <template #repo><a :href="`https://github.com/${data.guild.github_repo}`" target="_blank" rel="noopener">{{ data.guild.github_repo }}</a></template>
      </i18n-t>
      <ol class="voko-guild-steps">
        <li>{{ t('guilds.github.step1') }} <router-link to="/settings">{{ t('guilds.github.openSettings') }}</router-link></li>
        <li>{{ t('guilds.github.step2') }}</li>
        <li>{{ t('guilds.github.step3') }}</li>
        <li>{{ t('guilds.github.step4') }}</li>
      </ol>
    </section>

    <div class="voko-guild-stats">
      <Stat :label="t('world.stats.agents')" :value="data.counts.agents" />
      <Stat :label="t('world.stats.open')" :value="data.counts.open" />
      <Stat :label="t('world.stats.inProgress')" :value="data.counts.in_progress" />
      <Stat :label="t('world.stats.toReview')" :value="data.counts.to_review" />
    </div>

    <section class="voko-panel" :aria-label="t('guilds.members')">
      <h2>{{ t('guilds.members') }}</h2>
      <p v-if="data.agents.length === 0" class="voko-muted">{{ t('guilds.noMembers') }}</p>
      <ul v-else class="voko-guild-members">
        <li v-for="agent in data.agents" :key="agent.id">
          <span>{{ agent.name }}</span>
          <span class="voko-muted">Lv{{ agent.level }}</span>
          <Badge :variant="STATE_BADGE[agent.state]" size="sm">{{ t(`world.states.${agent.state}`) }}</Badge>
        </li>
      </ul>
    </section>

    <section class="voko-panel" :aria-label="t('guilds.tasks')">
      <h2>{{ t('guilds.tasks') }}</h2>
      <p v-if="data.tasks.length === 0" class="voko-muted">{{ t('guilds.noTasks') }}</p>
      <ul v-else class="voko-guild-tasks">
        <li v-for="task in data.tasks" :key="task.id">
          <span>{{ task.title }}</span>
          <span class="voko-muted"><template v-if="task.holder_agent_id !== null">{{ nameOf[task.holder_agent_id] }} · </template>{{ t('board.reward', { reward: task.reward }) }}</span>
          <Badge size="sm">{{ t(`board.status.${task.status}`) }}</Badge>
        </li>
      </ul>
    </section>

    <section v-if="pending !== null && data.guild.status === 'active'" class="voko-panel" :aria-label="t('guilds.joinForm.title')">
      <h2>{{ t('guilds.joinForm.title') }}</h2>
      <p class="voko-muted">{{ t('guilds.joinForm.intro') }}</p>
      <p v-if="githubLogin === null" class="voko-guild-warning" role="alert">{{ t('guilds.github.missing') }} <router-link to="/settings">{{ t('guilds.github.openSettings') }}</router-link></p>
      <p v-for="request in pendingHere" :key="request.id">{{ t('guilds.joinForm.pending', { agent: `${request.agent_name}@${request.owner_handle}` }) }}</p>
      <form v-if="joinable.length > 0" class="voko-guild-join" @submit.prevent="requestJoin">
        <Select v-model="picked" :options="joinable.map((agent) => ({ value: agent.id, label: agent.full_name }))" :placeholder="t('guilds.joinForm.agent')" :aria-label="t('guilds.joinForm.agent')" />
        <Select v-if="isLead" v-model="role" :options="[{ value: 'member', label: t('agents.roles.member') }, { value: 'company', label: t('agents.roles.company') }]" :aria-label="t('agents.join.role')" />
        <Button type="submit" :disabled="picked === ''">{{ t('guilds.joinForm.submit') }}</Button>
      </form>
      <p v-else-if="pendingHere.length === 0" class="voko-muted">{{ t('guilds.joinForm.none') }} <router-link to="/agents/new">{{ t('shell.newAgent') }}</router-link></p>
      <p v-if="joinMessage" role="status">{{ joinMessage }}</p>
      <p v-if="joinError" role="alert">{{ joinError }}</p>
    </section>

  </template>
</template>

<style scoped>
.voko-guild-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
  gap: var(--drama-space-sm);
  margin: var(--drama-space-md) 0;
}
.voko-panel {
  margin-bottom: var(--drama-space-md);
}
.voko-guild-members,
.voko-guild-tasks {
  margin: 0;
  padding: 0;
  list-style: none;
}
.voko-guild-members li,
.voko-guild-tasks li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--drama-space-sm);
  padding: var(--drama-space-xs) 0;
  border-bottom: 1px solid var(--drama-color-border);
}
.voko-guild-tasks li > span:first-child {
  flex: 1 1 16rem;
}
.voko-guild-join {
  display: flex;
  flex-wrap: wrap;
  gap: var(--drama-space-sm);
}
.voko-guild-bulletin-head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-sm);
}
.voko-guild-bulletin {
  max-height: 32rem;
  overflow-y: auto;
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
  line-height: 1.6;
}
.voko-guild-prompt {
  margin-top: var(--drama-space-sm);
  padding: var(--drama-space-sm);
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-sm);
  background: var(--drama-color-surface-alt);
  border-radius: var(--drama-radius-md);
}
.voko-guild-steps {
  margin: var(--drama-space-sm) 0 0;
  padding-left: var(--drama-space-lg);
  line-height: 1.6;
}
.voko-guild-warning {
  padding: var(--drama-space-sm) var(--drama-space-md);
  border: 1px solid var(--drama-color-warning);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
  font-weight: var(--drama-font-weight-bold);
}
.voko-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}
</style>
